import { useForm, useWatch } from "react-hook-form"
import { BLOCK, COMMUNITY, OCCUPANCY, OUTSTANDING_DUES, SEARCH, ZONE } from "./consts"
import { useGetCommunitiesDropdown, useGetPropertyBlocks, useGetPropertyReport, useGetPropertyReportExport, useGetPropertyZones } from "@/services";
import { useEffect, useMemo, useRef, useState } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { Button } from "@/components/ui";
import moment from "moment";
import { formatAmount, toApiSort } from "@/lib/utils";
import { toast } from "@/components/Toast";
import { useQueryClient } from "@tanstack/react-query";

const usePropertyReport = () => {  
  const form = useForm<any>({
    defaultValues: {
      [COMMUNITY]: ["all"],
      [ZONE]: ["all"],
      [BLOCK]: ["all"],
      [OCCUPANCY]: ["all"],
      [OUTSTANDING_DUES]: false,
      [SEARCH]: ""
    }
  });
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState("all");
  const [pageNumber, setPageNumber] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [sorting, setSorting] = useState([{ id: "createdAt", desc: true }]);
  const [openOccupantDialog, setOpenOccupantDialog] = useState<boolean>(false);
  const [openOutstandingDialog, setOpenOutstandingDialog] = useState<boolean>(false);
  const [openIncidentDialog, setOpenIncidentDialog] = useState<boolean>(false);
  const selectedRow = useRef<any>(null);
  const queryClient = useQueryClient();

  const communityId = useWatch({ control: form.control, name: COMMUNITY });
  const formData = useWatch({ control: form.control });
  const outstandingDues = useWatch({ control: form.control, name: OUTSTANDING_DUES });
  
  const filter = useMemo(() => ({
    [OUTSTANDING_DUES]: formData[OUTSTANDING_DUES],
    pageNumber,
    pageSize,
    [SEARCH]: formData[SEARCH],
    sort: toApiSort(sorting, { field: "createdAt", direction: "DESC" }),
    ...(formData[COMMUNITY]?.includes("all") ? {} : {[COMMUNITY]: formData[COMMUNITY]}),
    ...(formData[ZONE]?.includes("all") ? {} : {[ZONE]: formData[ZONE]}),
    ...(formData[BLOCK]?.includes("all") ? {} : {[BLOCK]: formData[BLOCK]}),
    ...(formData[OCCUPANCY]?.includes("all") ? {} : {[OCCUPANCY]: formData[OCCUPANCY]})
  }), [communityId, pageNumber, pageSize, formData, sorting]);

  const { data: communityList, isLoading: isLoadingCommunity } = useGetCommunitiesDropdown({});

  const { data: zoneList, isRefetching: isLoadingZoneList } = useGetPropertyZones();

  const { data: propertyBlock, isRefetching: isBlockLoading } = useGetPropertyBlocks();

  const onTabChange = (tab: any) => {
    setActiveTab(tab);
  }

  const columns = [
    {
      accessorKey: "location",
      header: <div>Location</div>,
      cell: ({row}: any) => {
        const {communityName, zoneName, blockName} = row?.original;

        return <div className="flex flex-col">
          <span>{communityName}</span>
          <span className="text-[10px] text-gray-500">Zone {zoneName} - Block {blockName}</span>
        </div>
      },
    },
    {
      accessorKey: "displayId",
      header: <div>Property ID</div>,
      accessorFn: (row: any) => String(row?.displayId || ""),
    },
    {
      accessorKey: "propertyName",
      header: <div>Property Name</div>,
      accessorFn: (row: any) => String(row?.propertyName || ""),
    },
    {
      accessorKey: "unitNumber",
      header: <div>Unit No</div>,
      accessorFn: (row: any) => String(row?.unitNumber || ""),
    },
    {
      accessorKey: "occupants",
      header: <div>Current Occupants</div>,
      cell: ({row}: any) => {
        const { occupants, startDate } = row?.original;

        return <Button variant="link" className="p-0" onClick={() => {
          selectedRow.current = {...row.original, selectedTab: "current"};;
          setOpenOccupantDialog(true);
        }}>
          <span>{occupants?.[0]?.fullName || ""} {occupants?.length > 1 ? `+ ${occupants?.length - 1}` : ""}</span>
          {startDate ? <span className="text-[10px] text-gray-500">Since {moment.utc(startDate).local().format("MM/DD/YYYY")}</span> : <></>}
        </Button>
      },
    },
    {
      accessorKey: "primaryPersonName",
      header: <div>Primary Person</div>,
      accessorFn: (row: any) => String(row?.primaryPersonName || ""),
    },
    {
      accessorKey: "primaryPersonMobileNo",
      header: <div>Mobile No.</div>,
      accessorFn: (row: any) => String(row?.primaryPersonMobileNo || ""),
    },
    {
      accessorKey: "incidentCount",
      header: <div># of Incidents</div>,
      cell: ({row}: any) => {
        const { incidentCount } = row?.original;

        return incidentCount ? <Button variant="link" className="p-0" onClick={() => {
          selectedRow.current = row.original;
          setOpenIncidentDialog(true);
        }}>
          {incidentCount || "0"} Total
        </Button> : <span>{incidentCount || "0"} Total</span>
      },
    },
    {
      accessorKey: "outstandingDues",
      header: <div>Outstanding Dues</div>,
      cell: ({row}: any) => {
        const { outstandingDues } = row?.original;

        return outstandingDues ? <Button variant="link" className="p-0" onClick={() => {
          selectedRow.current = row.original;
          setOpenOutstandingDialog(true);
        }}>
          {formatAmount(outstandingDues || "0")}
        </Button> : <span>{formatAmount(outstandingDues || "0")}</span>
      },
    },
    {
      accessorKey: "history",
      header: <div>History</div>,
      cell: ({row}: any) => {
        return <Button variant="link" className="p-0" onClick={() => {
          selectedRow.current = {...row.original, selectedTab: "history"};
          setOpenOccupantDialog(true);
        }}>
          View
        </Button>
      },
    },
  ];

  const onOutstandingCheckedChange = (checked: boolean) => {
    form.setValue(OUTSTANDING_DUES, checked);
    setPageNumber(0);
    setTimeout(() => refetchReportData(), 100);
  }

  const onClearAll = () => {
    form.reset({
      [COMMUNITY]: ["all"],
      [ZONE]: ["all"],
      [BLOCK]: ["all"],
      [OCCUPANCY]: ["all"],
      [OUTSTANDING_DUES]: false,
      [SEARCH]: ""
    });
  }

  const { data: reportData, isRefetching: isLoadingReport, refetch: refetchReportData } = useGetPropertyReport({
    data: { ...filter },
    queryParams: {
      enabled: false
    }
  });

  const onSubmit = () => {
    refetchReportData();
  }

  useEffect(() => {
    refetchReportData();
  }, [pageNumber, pageSize, sorting])

  const { data: reportExportData, isRefetching: isLoadingExportReport, refetch: refetchExportReportData } = useGetPropertyReportExport({
    data: { 
      ...filter, 
    },
    queryParams: {
      enabled: false
    }
  });

  useEffect(() => {
    if (!reportExportData) return;

    try {
      const blob = new Blob([reportExportData.data], {
        type: reportExportData.headers["content-type"],
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      const contentDisposition = reportExportData.headers["content-disposition"];
      let downloadFileName = `owner-report.xlsx`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^\"]+)"?/);
        if (match?.[1]) downloadFileName = match[1];
      }

      link.setAttribute("download", downloadFileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          "There is an issue while downloading the file"
      );
    } finally {
      queryClient.removeQueries({
        queryKey: ["owners-report-export"],
      });
    }
  }, [reportExportData]);

  const onExport = () => {
    refetchExportReportData();
  }
  
  return {
    form,
    isLoadingCommunity,
    isLoadingZoneList,
    isBlockLoading,
    communityList: communityList || [],
    zoneList: zoneList?.data || [],
    propertyBlock: propertyBlock?.data || [],
    activeTab, onTabChange, isMobile,
    columns,
    onOutstandingCheckedChange,
    outstandingDues,
    onClearAll,
    isLoadingReport,
    onSubmit,
    reportData,
    pageNumber,
    setPageNumber,
    pageSize,
    setPageSize,
    sorting,
    setSorting,
    openOccupantDialog,
    setOpenOccupantDialog,
    selectedRow: selectedRow.current,
    openOutstandingDialog,
    setOpenOutstandingDialog,
    openIncidentDialog,
    setOpenIncidentDialog,
    formData,
    onExport,
    isLoadingExportReport
  }
}

export default usePropertyReport;